<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import ConversationHeatmapContainer from '../settings/reports/components/heatmaps/ConversationHeatmapContainer.vue';
import PeriodSelector from './components/PeriodSelector.vue';
import MetricCard from './components/MetricCard.vue';
import NpsCard from './components/NpsCard.vue';
import PendingConversationsList from './components/PendingConversationsList.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();
const { currentAccount } = useAccount();

const overview = useMapGetter('painel/getOverview');
const pending = useMapGetter('painel/getPending');
const nps = useMapGetter('painel/getNps');
const uiFlags = useMapGetter('painel/getUiFlags');
const humanAttentionPending = useMapGetter(
  'painelHumanAttention/getPendingRequests'
);
const humanAttentionUiFlags = useMapGetter('painelHumanAttention/getUiFlags');

const currentRange = ref(null);

const isAdmin = computed(() => currentAccount.value?.role === 'administrator');

const accountId = computed(() => route.params.accountId);

const formatSeconds = seconds => {
  if (!seconds) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}min`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}min`;
};

const timeAgo = iso => {
  if (!iso) return '';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: ptBR });
  } catch {
    return '';
  }
};

const afterHoursPct = computed(() => {
  if (!overview.value) return null;
  return `${overview.value.after_hours_count} (${overview.value.after_hours_percentage}%)`;
});

const unconfiguredWarning = computed(() => {
  if (
    !overview.value ||
    overview.value.inboxes_without_working_hours_count === 0
  )
    return null;
  return t('PAINEL.OVERVIEW.UNCONFIGURED_INBOXES', {
    count: overview.value.inboxes_without_working_hours_count,
  });
});

const fetchAll = params => {
  currentRange.value = params;
  store.dispatch('painel/fetchOverview', params);
  store.dispatch('painel/fetchNps', params);
};

const onPeriodChange = params => fetchAll(params);

const attendHumanRequest = async req => {
  const attended = await store.dispatch('painelHumanAttention/attend', req.id);
  store.dispatch('painelHumanAttention/startAttendance', attended || req);
  if (req.conversation?.display_id || req.conversation?.id) {
    router.push({
      name: 'inbox_conversation',
      params: {
        conversation_id:
          req.conversation.display_id || req.conversation.id,
      },
    });
  }
};

onMounted(() => {
  store.dispatch('painel/fetchPending');
  store.dispatch('painelHumanAttention/fetchRequests', { status: 'pending' });
});
</script>

<template>
  <div
    class="flex h-full w-full min-w-0 flex-1 flex-col overflow-hidden bg-n-surface-1"
  >
    <div class="flex-1 overflow-y-auto">
      <div class="flex flex-col gap-6 p-4 md:p-6">
        <!-- Header -->
        <div class="flex items-center justify-between flex-wrap gap-3">
          <div class="flex items-center gap-2">
            <h1 class="text-xl font-semibold text-n-slate-12">
              {{ t('PAINEL.TITLE') }}
            </h1>
            <router-link
              v-if="isAdmin"
              :to="{ name: 'painel_configuracoes' }"
              class="flex items-center justify-center w-7 h-7 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-solid-2 transition"
              :title="t('SIDEBAR.PAINEL_CONFIGURACOES')"
            >
              <span class="i-lucide-settings text-base" />
            </router-link>
          </div>
          <PeriodSelector @change="onPeriodChange" />
        </div>

        <!-- Bloco 1: grade 2 colunas — conversas pendentes + atendimentos humanos -->
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <PendingConversationsList
            :conversations="pending"
            :loading="uiFlags.isFetchingPending"
          />

          <!-- Painel inline de atendimentos humanos -->
          <div
            class="flex flex-col gap-3 p-4 rounded-xl outline outline-1 outline-n-container bg-n-solid-2"
          >
            <div class="flex items-center justify-between">
              <div class="text-sm font-semibold text-n-slate-12">
                {{ t('PAINEL.HUMAN_ATTENTION.PAGE.TITLE') }}
              </div>
              <div class="flex items-center gap-2">
                <span
                  v-if="humanAttentionPending.length > 0"
                  class="px-2 py-0.5 text-xs rounded-full bg-n-ruby-3 text-n-ruby-9 font-medium"
                >
                  {{ humanAttentionPending.length }}
                </span>
                <router-link
                  :to="{ name: 'painel_human_attention' }"
                  class="text-xs text-n-slate-10 hover:text-n-slate-12 transition"
                >
                  {{ t('PAINEL.HUMAN_ATTENTION.INDEX.SEE_ALL') }}
                </router-link>
              </div>
            </div>

            <div
              v-if="humanAttentionUiFlags.isFetching"
              class="flex flex-col gap-2"
            >
              <div
                v-for="i in 3"
                :key="i"
                class="h-10 rounded bg-n-slate-3 animate-pulse"
              />
            </div>

            <div
              v-else-if="humanAttentionPending.length === 0"
              class="py-6 text-center text-sm text-n-slate-9"
            >
              {{ t('PAINEL.HUMAN_ATTENTION.PAGE.NO_PENDING') }}
            </div>

            <div
              v-else
              class="flex flex-col divide-y divide-n-slate-3 max-h-[11rem] overflow-y-auto pr-1"
            >
              <div
                v-for="req in humanAttentionPending"
                :key="req.id"
                class="flex items-center justify-between py-2 gap-2"
              >
                <div class="flex flex-col min-w-0">
                  <span class="text-sm font-medium text-n-slate-12 truncate">
                    {{ req.contact?.name || req.phone_number }}
                  </span>
                  <span class="text-xs text-n-slate-9">
                    {{ req.conversation?.inbox_name || req.phone_number }}
                    <span class="mx-1">&middot;</span>
                    {{ timeAgo(req.created_at) }}
                  </span>
                </div>
                <button
                  type="button"
                  :disabled="!!humanAttentionUiFlags.isAttending[req.id]"
                  class="shrink-0 px-2.5 py-1 text-xs font-semibold rounded-md bg-n-ruby-9 text-white hover:bg-n-ruby-10 transition disabled:opacity-50"
                  @click="attendHumanRequest(req)"
                >
                  {{ t('PAINEL.HUMAN_ATTENTION.PAGE.ATTEND') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Bloco 2: Cards de métricas -->
        <div
          v-if="unconfiguredWarning"
          class="px-3 py-2 text-xs rounded-lg bg-amber-3 text-amber-9"
        >
          {{ unconfiguredWarning }}
        </div>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            :label="t('PAINEL.OVERVIEW.TOTAL')"
            :value="overview ? overview.total_conversations : null"
            :loading="uiFlags.isFetchingOverview"
            :info-text="t('PAINEL.OVERVIEW.TOTAL_INFO')"
          />
          <MetricCard
            :label="t('PAINEL.OVERVIEW.AFTER_HOURS')"
            :value="afterHoursPct"
            :loading="uiFlags.isFetchingOverview"
            :info-text="t('PAINEL.OVERVIEW.AFTER_HOURS_INFO')"
          />
          <MetricCard
            :label="t('PAINEL.OVERVIEW.FIRST_RESPONSE')"
            :value="
              overview
                ? formatSeconds(overview.avg_first_response_time_seconds)
                : null
            "
            :loading="uiFlags.isFetchingOverview"
            :info-text="t('PAINEL.OVERVIEW.FIRST_RESPONSE_INFO')"
          />
          <NpsCard :nps="nps" :loading="uiFlags.isFetchingNps" />
        </div>

        <!-- Bloco 3: Heatmap -->
        <div class="mt-2 min-w-0">
          <ConversationHeatmapContainer />
        </div>
      </div>
    </div>
  </div>
</template>
